import type { CSSProperties, MutableRefObject, ReactNode, RefObject } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { LogLevel, useSeqLog } from '@/hooks/telemetry/use-seq-log';
import {
  CSCROLLABLE_SEQ_COMPONENT,
  CSCROLLABLE_VIEWPORT_BLOCK_SIZE_PROPERTY,
  CscrollableDebugReason,
  SCROLL_DIRECTION,
  SESSIONS_LIST_SCROLL_FADE_FEATURE,
} from './Cscrollable.constants';
import type { CscrollableProps, ScrollDirection } from './Cscrollable.types';
import { buildScrollDebugMeasurement } from './scrollDebugMeasurement';
import {
  applyScrollMask,
  buildScrollMaskState,
  getScrollProgressBucket,
  type ScrollMaskState,
} from './scrollMask';

interface UseCscrollableBehaviorInput {
  children: ReactNode;
  direction: ScrollDirection;
  fadeSize: number;
  horizontalFadeSize: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
  firstItemSpacing: CSSProperties['paddingTop'];
  externalScrollRef?: RefObject<HTMLDivElement | null>;
  onScrollCallback?: CscrollableProps['onScroll'];
  debugName?: string;
  debugContext?: Record<string, unknown>;
}

interface GetMaskStateInput {
  direction: ScrollDirection;
  fadeSize: number;
  horizontalFadeSize: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
}

interface EmitDebugMeasurementInput {
  element: HTMLDivElement;
  maskState?: ScrollMaskState;
  reason: CscrollableDebugReason;
  level?: LogLevel;
}

interface UseDebugMeasurementEmitterInput extends GetMaskStateInput {
  firstItemSpacing: CSSProperties['paddingTop'];
  debugName?: string;
  debugContext?: Record<string, unknown>;
  getMaskState: (element: HTMLDivElement) => ScrollMaskState;
}

interface UseScrollCheckerInput {
  debugName?: string;
  direction: ScrollDirection;
  getMaskState: (element: HTMLDivElement) => ScrollMaskState;
  onScrollCallback?: CscrollableProps['onScroll'];
  lastFadeStateRef: MutableRefObject<string | null>;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

interface EmitFadeStateMeasurementInput {
  debugName?: string;
  element: HTMLDivElement;
  maskState: ScrollMaskState;
  lastFadeStateRef: MutableRefObject<string | null>;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

interface EmitVerticalScrollCallbackInput {
  direction: ScrollDirection;
  element: HTMLDivElement;
  onScrollCallback?: CscrollableProps['onScroll'];
}

interface RequestScheduledScrollCheckInput {
  debugName?: string;
  element: HTMLDivElement;
  reason?: CscrollableDebugReason;
  rafRef: MutableRefObject<number | null>;
  lastScrollBucketRef: MutableRefObject<number | null>;
  checkScroll: (element: HTMLDivElement | null) => ScrollMaskState | null;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

interface EmitScheduledDebugMeasurementsInput {
  debugName?: string;
  element: HTMLDivElement;
  maskState: ScrollMaskState;
  reason?: CscrollableDebugReason;
  lastScrollBucketRef: MutableRefObject<number | null>;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

interface UseScrollLifecycleEffectInput {
  debugName?: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  rafRef: MutableRefObject<number | null>;
  lastScrollBucketRef: MutableRefObject<number | null>;
  checkScroll: (element: HTMLDivElement | null) => ScrollMaskState | null;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

interface UseChildrenLifecycleEffectInput {
  children: ReactNode;
  scrollRef: RefObject<HTMLDivElement | null>;
  checkScroll: (element: HTMLDivElement | null) => ScrollMaskState | null;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}

function useMaskStateGetter({
  direction,
  fadeSize,
  horizontalFadeSize,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
}: GetMaskStateInput): (element: HTMLDivElement) => ScrollMaskState {
  return useCallback((element: HTMLDivElement): ScrollMaskState => buildScrollMaskState({
    element,
    direction,
    fadeSize,
    horizontalFadeSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    verticalFadeTopSize,
  }), [
    direction,
    fadeSize,
    horizontalFadeSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    verticalFadeTopSize,
  ]);
}

function useDebugMeasurementEmitter({
  debugName,
  debugContext,
  direction,
  fadeSize,
  horizontalFadeSize,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
  firstItemSpacing,
  getMaskState,
}: UseDebugMeasurementEmitterInput): (input: EmitDebugMeasurementInput) => void {
  const logContext = useMemo(() => ({
    feature: SESSIONS_LIST_SCROLL_FADE_FEATURE,
    ...debugContext,
  }), [debugContext, debugName]);
  const log = useSeqLog(CSCROLLABLE_SEQ_COMPONENT, logContext);

  return useCallback(({
    element,
    maskState,
    reason,
    level = LogLevel.Debug,
  }: EmitDebugMeasurementInput): void => {
    if (!debugName) {
      return;
    }

    const resolvedMaskState = maskState ?? getMaskState(element);

    log(
      'Cscrollable: DOM behavior measured',
      buildScrollDebugMeasurement({
        element,
        direction,
        reason,
        maskImage: resolvedMaskState.maskImage,
        showStartFade: resolvedMaskState.showStartFade,
        showEndFade: resolvedMaskState.showEndFade,
        fadeSize,
        horizontalFadeSize,
        verticalFadeTopSize,
        verticalFadeBottomSize,
        verticalFadeMiddleOpacity,
        firstItemSpacing,
      }),
      level,
    );
  }, [
    debugName,
    direction,
    fadeSize,
    firstItemSpacing,
    getMaskState,
    horizontalFadeSize,
    log,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    verticalFadeTopSize,
  ]);
}

function getFadeStateKey({ maskState }: { maskState: ScrollMaskState }): string {
  return `${maskState.showStartFade}:${maskState.showEndFade}:${maskState.scrollSize}:${maskState.clientSize}`;
}

function emitFadeStateChangeMeasurement({
  debugName,
  element,
  maskState,
  lastFadeStateRef,
  emitDebugMeasurement,
}: EmitFadeStateMeasurementInput): void {
  if (!debugName) {
    return;
  }

  const fadeStateKey = getFadeStateKey({ maskState });
  if (fadeStateKey === lastFadeStateRef.current) {
    return;
  }

  lastFadeStateRef.current = fadeStateKey;
  emitDebugMeasurement({
    element,
    maskState,
    reason: CscrollableDebugReason.FadeStateChanged,
    level: LogLevel.Info,
  });
}

function emitVerticalScrollCallback({
  direction,
  element,
  onScrollCallback,
}: EmitVerticalScrollCallbackInput): void {
  if (direction !== SCROLL_DIRECTION.Vertical) {
    return;
  }

  if (!onScrollCallback) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = element;
  onScrollCallback(scrollTop, scrollHeight, clientHeight);
}

function useScrollChecker({
  debugName,
  direction,
  getMaskState,
  onScrollCallback,
  lastFadeStateRef,
  emitDebugMeasurement,
}: UseScrollCheckerInput): (element: HTMLDivElement | null) => ScrollMaskState | null {
  return useCallback((element: HTMLDivElement | null): ScrollMaskState | null => {
    if (!element) {
      return null;
    }

    const maskState = getMaskState(element);
    applyScrollMask(element, maskState.maskImage, maskState.maskComposite);
    emitFadeStateChangeMeasurement({
      debugName,
      element,
      maskState,
      lastFadeStateRef,
      emitDebugMeasurement,
    });
    emitVerticalScrollCallback({ direction, element, onScrollCallback });

    return maskState;
  }, [debugName, direction, emitDebugMeasurement, getMaskState, lastFadeStateRef, onScrollCallback]);
}

function emitScrollBucketMeasurement({
  element,
  maskState,
  lastScrollBucketRef,
  emitDebugMeasurement,
}: Pick<
  EmitScheduledDebugMeasurementsInput,
  'element' | 'maskState' | 'lastScrollBucketRef' | 'emitDebugMeasurement'
>): void {
  const scrollBucket = getScrollProgressBucket({
    scrollPosition: maskState.scrollPosition,
    scrollSize: maskState.scrollSize,
    clientSize: maskState.clientSize,
  });

  if (scrollBucket === lastScrollBucketRef.current) {
    return;
  }

  lastScrollBucketRef.current = scrollBucket;
  emitDebugMeasurement({
    element,
    maskState,
    reason: CscrollableDebugReason.ScrollSample,
  });
}

function emitScheduledDebugMeasurements({
  debugName,
  element,
  maskState,
  reason,
  lastScrollBucketRef,
  emitDebugMeasurement,
}: EmitScheduledDebugMeasurementsInput): void {
  if (!debugName) {
    return;
  }

  if (reason) {
    emitDebugMeasurement({ element, maskState, reason });
  }

  emitScrollBucketMeasurement({
    element,
    maskState,
    lastScrollBucketRef,
    emitDebugMeasurement,
  });
}

function requestScheduledScrollCheck({
  debugName,
  element,
  reason,
  rafRef,
  lastScrollBucketRef,
  checkScroll,
  emitDebugMeasurement,
}: RequestScheduledScrollCheckInput): void {
  if (rafRef.current != null) {
    return;
  }

  rafRef.current = requestAnimationFrame(() => {
    const maskState = checkScroll(element);
    if (maskState) {
      emitScheduledDebugMeasurements({
        debugName,
        element,
        maskState,
        reason,
        lastScrollBucketRef,
        emitDebugMeasurement,
      });
    }

    rafRef.current = null;
  });
}

/**
 * Publishes the scrollport height as a custom property on the scroll element.
 * Descendants that need viewport-relative geometry (e.g. the chat spine
 * dissolve, whose mask is anchored to a fixed line in the scrollport) read it
 * from CSS instead of measuring on every scroll frame.
 */
function publishViewportBlockSize({ element }: { element: HTMLDivElement }): void {
  element.style.setProperty(
    CSCROLLABLE_VIEWPORT_BLOCK_SIZE_PROPERTY,
    `${element.clientHeight}px`,
  );
}

function cancelPendingScrollCheck({ rafRef }: { rafRef: MutableRefObject<number | null> }): void {
  if (rafRef.current == null) {
    return;
  }

  cancelAnimationFrame(rafRef.current);
  rafRef.current = null;
}

function emitInitialMeasurement({
  debugName,
  element,
  maskState,
  emitDebugMeasurement,
}: {
  debugName?: string;
  element: HTMLDivElement;
  maskState: ScrollMaskState | null;
  emitDebugMeasurement: (input: EmitDebugMeasurementInput) => void;
}): void {
  if (!debugName || !maskState) {
    return;
  }

  emitDebugMeasurement({
    element,
    maskState,
    reason: CscrollableDebugReason.InitialMount,
    level: LogLevel.Info,
  });
}

function useScrollLifecycleEffect({
  debugName,
  scrollRef,
  rafRef,
  lastScrollBucketRef,
  checkScroll,
  emitDebugMeasurement,
}: UseScrollLifecycleEffectInput): void {
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const scheduleScrollCheck = (reason?: CscrollableDebugReason): void => {
      requestScheduledScrollCheck({
        debugName,
        element,
        reason,
        rafRef,
        lastScrollBucketRef,
        checkScroll,
        emitDebugMeasurement,
      });
    };
    const handleScroll = (): void => {
      scheduleScrollCheck();
    };

    const initialMaskState = checkScroll(element);
    publishViewportBlockSize({ element });
    emitInitialMeasurement({ debugName, element, maskState: initialMaskState, emitDebugMeasurement });
    element.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      publishViewportBlockSize({ element });
      scheduleScrollCheck(CscrollableDebugReason.ResizeObserved);
    });
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      cancelPendingScrollCheck({ rafRef });
    };
  }, [checkScroll, debugName, emitDebugMeasurement, lastScrollBucketRef, rafRef, scrollRef]);
}

function useChildrenLifecycleEffect({
  children,
  scrollRef,
  checkScroll,
  emitDebugMeasurement,
}: UseChildrenLifecycleEffectInput): void {
  useLayoutEffect(() => {
    const element = scrollRef.current;
    const maskState = checkScroll(element);
    if (element && maskState) {
      emitDebugMeasurement({
        element,
        maskState,
        reason: CscrollableDebugReason.ChildrenChanged,
        level: LogLevel.Info,
      });
    }
  }, [children, checkScroll, emitDebugMeasurement, scrollRef]);
}

export function useCscrollableBehavior({
  children,
  direction,
  fadeSize,
  horizontalFadeSize,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
  firstItemSpacing,
  externalScrollRef,
  onScrollCallback,
  debugName,
  debugContext,
}: UseCscrollableBehaviorInput): RefObject<HTMLDivElement | null> {
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const lastScrollBucketRef = useRef<number | null>(null);
  const lastFadeStateRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const getMaskState = useMaskStateGetter({
    direction,
    fadeSize,
    horizontalFadeSize,
    verticalFadeTopSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
  });
  const emitDebugMeasurement = useDebugMeasurementEmitter({
    debugName,
    debugContext,
    direction,
    fadeSize,
    horizontalFadeSize,
    verticalFadeTopSize,
    verticalFadeBottomSize,
    verticalFadeMiddleOpacity,
    firstItemSpacing,
    getMaskState,
  });
  const checkScroll = useScrollChecker({
    debugName,
    direction,
    getMaskState,
    onScrollCallback,
    lastFadeStateRef,
    emitDebugMeasurement,
  });

  useScrollLifecycleEffect({
    debugName,
    scrollRef,
    rafRef,
    lastScrollBucketRef,
    checkScroll,
    emitDebugMeasurement,
  });
  useChildrenLifecycleEffect({ children, scrollRef, checkScroll, emitDebugMeasurement });

  return scrollRef;
}
